import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { PosiblesDuplicados } from '../components/PosiblesDuplicados';
import { Badge } from '../components/Badge';
import { TablaIvaPorAlicuota } from '../components/TablaIvaVenta';
import { FacturaOrigenNota, NotaCreditoVentaModal, NotaDebitoVentaModal } from '../components/NotasVenta';
import { LETRAS_COMPROBANTE, numeroComprobanteVisible } from '../lib/comprobanteCompra';
import { pideNumeroComprobante } from '../lib/comprobanteVenta';
import { etiquetaCondicionIva } from '../lib/condicionIva';
import { resumenIvaVenta } from '../lib/ivaVenta';
import { montoAPagar } from '../lib/montoAPagar';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useCamposObligatorios } from '../hooks/useCamposObligatorios';
import { Obligatorio, LeyendaObligatorio } from '../components/Obligatorio';

interface FacturaVenta {
  id: number;
  /** factura | nota_credito | nota_debito */
  tipo?: string | null;
  numeroFactura?: string;
  letra?: string | null;
  puntoVenta?: number | null;
  numeroComprobante?: number | null;
  tipoComprobante?: string | null;
  cae?: string | null;
  caeVencimiento?: string | null;
  fechaEmision: string;
  estado: string;
  fechaVencimiento?: string;
  montoTotal: number;
  montoCobrado: number;
  /** En una factura: lo que le descontaron sus notas de crédito (baja lo que queda por cobrar). */
  notasCreditoAplicadas?: number | string | null;
  /** En una nota de crédito: lo aplicado a su factura de origen, el saldo a favor que dejó y su estado propio. */
  montoAplicadoOrigen?: number | string | null;
  saldoCredito?: number | string | null;
  estadoCredito?: string | null;
  facturaOrigen?: {
    id: number; numeroFactura?: string | null; montoTotal?: number | string; estado?: string; ordenVenta?: { canal: string } | null;
  } | null;
}

interface CondicionRef { id: number; nombre: string }
interface CuotaPrevia { numero: number; fechaVencimiento: string; monto: number; interesPorcentaje: number }
interface Cronograma {
  origen: 'manual' | 'mercadolibre' | 'elegida' | 'cliente' | 'contado';
  condicion: CondicionRef | null;
  condicionCliente: CondicionRef | null;
  cuotas: CuotaPrevia[];
}
interface CuotaFactura { id: number; numero: number; fechaVencimiento: string; monto: number | string; montoCobrado: number | string; estado: string }
interface FacturaVentaDetalle extends FacturaVenta {
  cliente?: { id: number; nombre: string; cuit?: string };
  condicionPago?: CondicionRef | null;
  ordenVentaId?: number | null;
  cuotas?: CuotaFactura[];
  subtotalNeto?: number | string | null;
  montoIva?: number | string | null;
  alicuotasIva?: Array<{ id: number; alicuota: number | string; netoGravado: number | string; iva: number | string }>;
  ordenVenta?: { id: number; canal: string } | null;
  notasAsociadas?: Array<{
    id: number; tipo: string; numeroFactura?: string | null; fechaEmision: string; montoTotal: number | string; estado: string;
    montoAplicadoOrigen?: number | string; saldoCredito?: number | string; estadoCredito?: string | null;
  }>;
  /** Saldo a favor: el que recibió la factura (de notas de crédito del cliente) y, en una NC, a qué facturas fue */
  aplicacionesCreditoRecibidas?: AplicacionCreditoVenta[];
  aplicacionesCreditoConsumidas?: AplicacionCreditoVenta[];
  /** En una NC: la parte de su saldo que se le devolvió en plata al cliente */
  devolucionesSaldo?: Array<{
    id: number; monto: number | string;
    devolucion?: { id: number; fecha: string; medio?: string | null; usuario?: string | null; cuenta?: { id: number; nombre: string } | null } | null;
  }>;
}
/** Tramo de saldo a favor aplicado: la otra punta es la NC (en la factura) o la factura (en la NC). */
interface AplicacionCreditoVenta {
  id: number; monto: number | string; createdAt: string; usuario?: string | null;
  notaCredito?: { id: number; numeroFactura?: string | null } | null;
  facturaVenta?: { id: number; numeroFactura?: string | null } | null;
}
/** Letra sugerida e ítems de la OV con su alícuota (GET /facturas-venta/datos-fiscales). */
interface DatosFiscales {
  letraSugerida: string | null;
  condicionIvaCliente: string | null;
  canal: string;
  total: number;
  items: Array<{ descripcion: string; skuInterno?: string | null; total: number; alicuota: number }>;
}

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({
  ordenVentaId: '', clienteId: '', numeroFactura: '', fechaEmision: today(),
  condicionPagoId: '', vencimientoManual: false, fechaVencimiento: '',
  // null = la letra sugerida por la condición de IVA del cliente; '' = sin letra.
  letra: null as string | null, puntoVenta: '', numeroComprobante: '', cae: '', caeVencimiento: '',
});
const fechaCorta = (f?: string) => (f ? String(f).slice(0, 10).split('-').reverse().join('/') : '—');
const pesos = (n: number | string) => `$ ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const esNotaCredito = (f: { tipo?: string | null }) => f.tipo === 'nota_credito';
const esNota = (f: { tipo?: string | null }) => f.tipo === 'nota_credito' || f.tipo === 'nota_debito';
/** Lo que queda por cobrar: total − notas de crédito aplicadas − cobrado (una nota de crédito no se cobra). */
const saldoACobrar = (f: FacturaVenta) => (esNotaCredito(f) || f.estado === 'cancelada'
  ? 0
  : Math.max(0, Math.round((montoAPagar(f) - Number(f.montoCobrado || 0)) * 100) / 100));
/** Estado visible: en la nota de crédito, su estado propio (aplicada / con saldo a favor). */
const estadoVisible = (f: { tipo?: string | null; estado: string; estadoCredito?: string | null }) => {
  if (!esNotaCredito(f) || f.estado === 'cancelada') return f.estado;
  return f.estadoCredito === 'con_saldo' ? 'con saldo a favor' : 'aplicada';
};
const BadgeTipo = ({ tipo }: { tipo?: string | null }) => (tipo === 'nota_credito'
  ? <Badge tone="green">NC</Badge>
  : tipo === 'nota_debito' ? <Badge tone="amber">ND</Badge> : null);

/** Estado de la cuota para mostrar: "vencida" si pasó la fecha con saldo (no se guarda). */
function estadoCuota(c: CuotaFactura): string {
  const saldo = Number(c.monto) - Number(c.montoCobrado);
  if (saldo > 0.005 && String(c.fechaVencimiento).slice(0, 10) < today()) return 'vencida';
  return c.estado;
}

export function FacturasVentaPage() {
  const { data, loading, refetch } = useApi<FacturaVenta[]>('/api/v3/facturas-venta');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const filtered = (data ?? []).filter(row =>
    Object.entries(filters).every(([key, val]) => !val || String((row as any)[key] ?? '').toLowerCase().includes(val.toLowerCase())),
  );
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const cerrarNueva = () => { setOpen(false); setForm(emptyForm()); };
  const cierreNueva = useCierreConfirmado(useHayCambios(form, open), cerrarNueva);
  // Obligatorios (backend: CreateFacturaVentaDto, facturarDesdeOrden e identidadComprobanteVenta): la OV (sin ella el
  // backend haría una factura directa, que pide ítems que esta pantalla no carga); punto de venta y número, los dos o
  // ninguno. El cliente sólo si la OV es de mostrador sin cliente: eso lo avisa el backend.
  const obligatorios = useCamposObligatorios(open);
  const pideNumero = pideNumeroComprobante(form.puntoVenta, form.numeroComprobante);
  const toast = useToast();
  const { can } = useAuth();
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const { data: detalle, loading: detalleLoading, refetch: refetchDetalle } = useApi<FacturaVentaDetalle>(detalleId ? `/api/v3/facturas-venta/${detalleId}` : null);
  const [notaCreditoDe, setNotaCreditoDe] = useState<FacturaOrigenNota | null>(null);
  const [notaDebitoDe, setNotaDebitoDe] = useState<FacturaOrigenNota | null>(null);
  const [anulando, setAnulando] = useState(false);
  const [revirtiendoAplicacion, setRevirtiendoAplicacion] = useState<number | null>(null);
  // Revierte un tramo de saldo a favor: la NC recupera el saldo y la factura vuelve a deber ese monto
  const revertirAplicacion = async (facturaId: number, aplicacionId: number) => {
    if (!window.confirm('¿Revertir el saldo a favor aplicado? La nota de crédito recupera el saldo y la factura vuelve a deber ese monto.')) return;
    setRevirtiendoAplicacion(aplicacionId);
    try {
      await api.delete(`/api/v3/facturas-venta/${facturaId}/aplicaciones-credito/${aplicacionId}`);
      toast.addToast('Saldo a favor revertido', 'success');
      refetch();
      refetchDetalle();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setRevirtiendoAplicacion(null);
    }
  };
  const notaCreada = () => {
    setNotaCreditoDe(null);
    setNotaDebitoDe(null);
    refetch();
    refetchDetalle();
  };
  const anularNota = async () => {
    if (!detalle) return;
    const aviso = esNotaCredito(detalle)
      ? '¿Anular la nota de crédito? Se revierte lo aplicado a la factura de origen y, si la nota devolvió mercadería (notas anteriores a los remitos de devolución), sale del stock.'
      : '¿Anular la nota de débito?';
    if (!window.confirm(aviso)) return;
    setAnulando(true);
    try {
      await api.patch(`/api/v3/facturas-venta/${detalle.id}`, { estado: 'cancelada' });
      toast.addToast(esNotaCredito(detalle) ? 'Nota de crédito anulada' : 'Nota de débito anulada', 'success');
      refetch();
      refetchDetalle();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setAnulando(false);
    }
  };
  const { data: cpResponse } = useApi<any>(open ? '/api/v3/condiciones-pago?activo=true' : null);
  const condicionesPago: Array<CondicionRef & { tipo?: string }> = (cpResponse as any)?.data ?? [];

  // Vista previa del cronograma: misma resolución que el backend al emitir.
  const usaManual = form.vencimientoManual && !!form.fechaVencimiento;
  const cronogramaParams = new URLSearchParams();
  if (form.ordenVentaId) cronogramaParams.set('ordenVentaId', form.ordenVentaId);
  if (form.clienteId) cronogramaParams.set('clienteId', form.clienteId);
  if (form.fechaEmision) cronogramaParams.set('fechaEmision', form.fechaEmision);
  if (form.condicionPagoId) cronogramaParams.set('condicionPagoId', form.condicionPagoId);
  if (usaManual) cronogramaParams.set('fechaVencimiento', form.fechaVencimiento);
  const { data: cronograma, error: cronogramaError } = useApi<Cronograma>(
    open && form.ordenVentaId ? `/api/v3/facturas-venta/cronograma?${cronogramaParams}` : null,
  );
  const esMercadoLibre = cronograma?.origen === 'mercadolibre';
  const condicionCliente = cronograma?.condicionCliente ?? null;
  const difiereDelCliente = !esMercadoLibre && !usaManual && !!form.condicionPagoId
    && Number(form.condicionPagoId) !== (condicionCliente?.id ?? null);
  const nombreCondicion = (id: string) => condicionesPago.find(cp => String(cp.id) === id)?.nombre ?? `#${id}`;

  // Datos fiscales: letra sugerida por la condición de IVA del cliente e IVA por alícuota (precios con IVA incluido).
  const fiscalesParams = new URLSearchParams();
  if (form.ordenVentaId) fiscalesParams.set('ordenVentaId', form.ordenVentaId);
  if (form.clienteId) fiscalesParams.set('clienteId', form.clienteId);
  const { data: fiscales } = useApi<DatosFiscales>(
    open && form.ordenVentaId ? `/api/v3/facturas-venta/datos-fiscales?${fiscalesParams}` : null,
  );
  const letraSugerida = fiscales?.letraSugerida ?? null;
  const letraElegida = form.letra ?? letraSugerida ?? '';
  const avisoLetra = letraSugerida && letraElegida !== letraSugerida
    ? (fiscales?.condicionIvaCliente
      ? `El cliente es ${etiquetaCondicionIva(fiscales.condicionIvaCliente)}: la factura suele ser ${letraSugerida}`
      : `Orden de MercadoLibre sin condición de IVA del cliente: la factura suele ser ${letraSugerida}`)
    : null;
  const resumenIva = fiscales ? resumenIvaVenta(fiscales.items, fiscales.total) : null;
  const pv = Number(form.puntoVenta);
  const nro = Number(form.numeroComprobante);
  const pvValido = form.puntoVenta !== '' && Number.isInteger(pv) && pv >= 1 && pv <= 99999;
  const nroValido = form.numeroComprobante !== '' && Number.isInteger(nro) && nro >= 1 && nro <= 99999999;
  const numeroPrevio = pvValido && nroValido ? numeroComprobanteVisible(letraElegida || null, pv, nro) : null;
  const errorIdentidad = (form.puntoVenta !== '' && !pvValido) ? 'El punto de venta va de 1 a 99999'
    : (form.numeroComprobante !== '' && !nroValido) ? 'El número va de 1 a 99999999'
      : (pideNumero && (form.puntoVenta === '' || form.numeroComprobante === '')) ? 'Indicá el punto de venta y el número (los dos o ninguno)'
        : (form.cae !== '' && !/^\d{14}$/.test(form.cae)) ? 'El CAE tiene 14 dígitos'
          : null;

  const handleCreate = async () => {
    if (!obligatorios.validar()) return;
    if (errorIdentidad) {
      toast.addToast(errorIdentidad, 'error');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/v3/facturas-venta', {
        ordenVentaId: Number(form.ordenVentaId),
        clienteId: form.clienteId ? Number(form.clienteId) : undefined,
        numeroFactura: numeroPrevio ? undefined : (form.numeroFactura || undefined),
        letra: letraElegida || undefined,
        puntoVenta: numeroPrevio ? pv : undefined,
        numeroComprobante: numeroPrevio ? nro : undefined,
        cae: form.cae || undefined,
        caeVencimiento: form.caeVencimiento || undefined,
        // Vacía: no se manda y el backend usa la fecha de hoy (un texto vacío no es una fecha y lo rechaza)
        fechaEmision: form.fechaEmision || undefined,
        // Vencimiento manual: una cuota en esa fecha. Si no, la condición elegida o la del cliente.
        fechaVencimiento: usaManual ? form.fechaVencimiento : undefined,
        condicionPagoId: !usaManual && form.condicionPagoId ? Number(form.condicionPagoId) : undefined,
      });
      toast.addToast('Factura de venta emitida', 'success');
      cerrarNueva();
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { key: 'id', header: '#' },
    { key: 'numeroFactura', header: 'Comprobante', render: (r: FacturaVenta) => (
      <span className="inline-flex flex-wrap items-center gap-1">
        <BadgeTipo tipo={r.tipo} />
        {r.numeroFactura || '—'}
        {esNota(r) && r.facturaOrigen && <span className="text-xs text-slate-500">{`de ${r.facturaOrigen.numeroFactura || `#${r.facturaOrigen.id}`}`}</span>}
      </span>
    ) },
    { key: 'fechaEmision', header: 'Emisión', render: (r: FacturaVenta) => String(r.fechaEmision).slice(0, 10) },
    { key: 'fechaVencimiento', header: 'Vencimiento', render: (r: FacturaVenta) => (esNotaCredito(r) ? '—' : fechaCorta(r.fechaVencimiento)) },
    { key: 'estado', header: 'Estado', render: (r: FacturaVenta) => estadoVisible(r) },
    { key: 'montoTotal', header: 'Total', render: (r: FacturaVenta) => (
      <span className="inline-flex flex-wrap items-center gap-1">
        {esNotaCredito(r)
          ? <span className="text-emerald-700">{`−$ ${Number(r.montoTotal).toLocaleString('es-AR')}`}</span>
          : `$ ${Number(r.montoTotal).toLocaleString('es-AR')}`}
        {Number(r.notasCreditoAplicadas || 0) > 0 && (
          <span className="text-xs text-emerald-700" title="Notas de crédito aplicadas: se cobra el total menos este monto">
            {`−$ ${Number(r.notasCreditoAplicadas).toLocaleString('es-AR')} NC`}
          </span>
        )}
      </span>
    ) },
    { key: 'montoCobrado', header: 'Cobrado', render: (r: FacturaVenta) => (esNotaCredito(r) ? '—' : `$ ${Number(r.montoCobrado).toLocaleString('es-AR')}`) },
    { key: 'saldo', header: 'Saldo', render: (r: FacturaVenta) => (esNotaCredito(r) ? '—' : `$ ${saldoACobrar(r).toLocaleString('es-AR')}`) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Facturas de Venta</h1>
          <HelpTip tips={['Emití una factura desde una orden de venta confirmada', 'Al facturar se consume el stock reservado', 'Desde el detalle de una factura podés registrar una nota de crédito (acredita renglones de la factura; no mueve stock: la mercadería devuelta se ingresa con un remito de devolución en Remitos) o una nota de débito', 'La nota de crédito baja lo que queda por cobrar de su factura; lo que excede queda como saldo a favor del cliente', 'El saldo a favor se aplica a otras facturas del cliente desde Cobros; en el detalle de la factura se ve lo aplicado y se puede revertir', 'Registrá el comprobante emitido (por MercadoLibre u otro sistema): letra, punto de venta, número y CAE', 'Los precios incluyen IVA: el neto y el IVA se calculan por alícuota', 'Los vencimientos salen de la condición de pago del cliente (o la que elijas); las ventas de MercadoLibre se cobran por MercadoPago', 'El estado pasa a pagada cuando se cobra el total']} />
          <p className="text-slate-500 text-sm mt-1">Facturación del lado venta</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PosiblesDuplicados endpoint="/api/v3/facturas-venta/posibles-duplicados" contraparte="cliente" />
          <Button
            onClick={() => {
              setForm(emptyForm());
              setOpen(true);
            }}
            disabled={!can('facturas-venta', 'crear')}
            title={!can('facturas-venta', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}
          >
            + Facturar OV
          </Button>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        onRowClick={(r: FacturaVenta) => setDetalleId(r.id)}
        filters={filters}
        onFilterChange={(k, v) => setFilters(f => v ? { ...f, [k]: v } : Object.fromEntries(Object.entries(f).filter(([key]) => key !== k)))}
      />
      <Modal open={open} cierre={cierreNueva} title="Facturar Orden de Venta" size="md">
        <div {...obligatorios.contenedor} className="space-y-4">
          <LeyendaObligatorio />
          <div>
            <label className="block text-sm font-medium mb-1">ID Orden de Venta<Obligatorio /></label>
            <input type="number" aria-label="Orden de venta" required value={form.ordenVentaId} onChange={e => setForm({ ...form, ordenVentaId: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Cliente (solo si la OV es mostrador sin cliente)</label>
            <input type="number" aria-label="Cliente" placeholder="ID cliente" value={form.clienteId} onChange={e => setForm({ ...form, clienteId: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Letra</label>
              <select value={letraElegida} onChange={e => setForm({ ...form, letra: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                <option value="">Sin letra</option>
                {LETRAS_COMPROBANTE.map(l => <option key={l} value={l}>{l}{l === letraSugerida ? ' (sugerida)' : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Punto de venta{pideNumero && <Obligatorio />}</label>
              <input type="number" min={1} max={99999} aria-label="Punto de venta" required={pideNumero} value={form.puntoVenta} onChange={e => setForm({ ...form, puntoVenta: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Número{pideNumero && <Obligatorio />}</label>
              <input type="number" min={1} max={99999999} aria-label="Número del comprobante" required={pideNumero} value={form.numeroComprobante} onChange={e => setForm({ ...form, numeroComprobante: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
          {avisoLetra && <p className="text-amber-700 text-xs">{`Atención: ${avisoLetra}.`}</p>}
          {numeroPrevio && <p className="text-sm text-slate-600">Comprobante: <span className="font-medium">{numeroPrevio}</span></p>}
          {errorIdentidad && <p className="text-red-600 text-sm">{errorIdentidad}</p>}
          {!numeroPrevio && (
            <div>
              <label className="block text-sm font-medium mb-1">Número (texto libre, sin punto de venta y número)</label>
              <input value={form.numeroFactura} onChange={e => setForm({ ...form, numeroFactura: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Emisión</label>
              <input type="date" aria-label="Emisión" value={form.fechaEmision} onChange={e => setForm({ ...form, fechaEmision: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CAE</label>
              <input inputMode="numeric" maxLength={14} placeholder="14 dígitos" value={form.cae} onChange={e => setForm({ ...form, cae: e.target.value.trim() })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Vencimiento del CAE</label>
              <input type="date" value={form.caeVencimiento} onChange={e => setForm({ ...form, caeVencimiento: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
          {esMercadoLibre ? (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
              Orden de MercadoLibre: se cobra por MercadoPago, sin condición de pago. La factura queda contado a la fecha de emisión y el flujo de caja toma el cobro de MercadoPago.
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Condición de pago</label>
                <select
                  value={form.condicionPagoId}
                  disabled={usaManual}
                  onChange={e => setForm({ ...form, condicionPagoId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg disabled:bg-slate-100"
                >
                  <option value="">{`La del cliente: ${condicionCliente?.nombre ?? 'contado (sin condición)'}`}</option>
                  {condicionesPago.map(cp => <option key={cp.id} value={cp.id}>{cp.nombre}{cp.tipo ? ` (${cp.tipo})` : ''}</option>)}
                </select>
                {difiereDelCliente && (
                  <p className="text-amber-700 text-xs mt-1">
                    {`Atención: la condición elegida (${nombreCondicion(form.condicionPagoId)}) es distinta de la del cliente (${condicionCliente?.nombre ?? 'contado'}).`}
                  </p>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.vencimientoManual} onChange={e => setForm({ ...form, vencimientoManual: e.target.checked })} />
                  Vencimiento manual (una sola cuota, ignora la condición)
                </label>
                {form.vencimientoManual && (
                  <input type="date" value={form.fechaVencimiento} onChange={e => setForm({ ...form, fechaVencimiento: e.target.value })} className="mt-2 w-full px-3 py-2 border rounded-lg" />
                )}
              </div>
            </div>
          )}
          {form.ordenVentaId && (
            <div>
              <p className="text-sm font-medium mb-1">
                Cronograma de cobro
                {cronograma?.condicion && <span className="font-normal text-slate-500">{` · ${cronograma.condicion.nombre}`}</span>}
                {cronograma?.origen === 'manual' && <span className="font-normal text-slate-500"> · vencimiento manual</span>}
                {cronograma?.origen === 'contado' && <span className="font-normal text-slate-500"> · contado</span>}
              </p>
              {cronogramaError ? (
                <p className="text-red-600 text-sm">{cronogramaError}</p>
              ) : cronograma ? (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-500"><th className="py-1">Cuota</th><th>Vencimiento</th><th className="text-right">Monto</th></tr></thead>
                  <tbody>
                    {cronograma.cuotas.map(c => (
                      <tr key={c.numero} className="border-t">
                        <td className="py-1">{`${c.numero}/${cronograma.cuotas.length}`}</td>
                        <td>{fechaCorta(c.fechaVencimiento)}</td>
                        <td className="text-right">{pesos(c.monto)}{c.interesPorcentaje > 0 ? ` (+${c.interesPorcentaje}% int.)` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-slate-500 text-sm">Calculando…</p>
              )}
            </div>
          )}
          {resumenIva && resumenIva.porAlicuota.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-1">IVA por alícuota <span className="font-normal text-slate-500">· precios con IVA incluido</span></p>
              <TablaIvaPorAlicuota filas={resumenIva.porAlicuota} />
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={cierreNueva.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
            <Button onClick={handleCreate} loading={submitting}>
              {submitting ? 'Emitiendo...' : 'Emitir'}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        open={detalleId !== null}
        onClose={() => setDetalleId(null)}
        title={`${detalle?.tipo === 'nota_credito' ? 'Nota de crédito' : detalle?.tipo === 'nota_debito' ? 'Nota de débito' : 'Factura de venta'} ${detalle?.numeroFactura || `#${detalleId ?? ''}`}`}
        size="md"
      >
        {detalleLoading || !detalle ? (
          <p className="text-slate-500 text-sm">Cargando…</p>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-slate-500">Cliente:</span> <span className="font-medium">{detalle.cliente?.nombre ?? '—'}</span></div>
              <div><span className="text-slate-500">Estado:</span> <span className="font-medium">{estadoVisible(detalle)}</span></div>
              <div><span className="text-slate-500">Emisión:</span> <span className="font-medium">{fechaCorta(detalle.fechaEmision)}</span></div>
              {!esNotaCredito(detalle) && <div><span className="text-slate-500">Vencimiento:</span> <span className="font-medium">{fechaCorta(detalle.fechaVencimiento)}</span></div>}
              <div><span className="text-slate-500">Total:</span> <span className="font-medium">{esNotaCredito(detalle) ? `−${pesos(detalle.montoTotal)}` : pesos(detalle.montoTotal)}</span></div>
              {esNota(detalle) && detalle.facturaOrigen && (
                <div className="col-span-2">
                  <span className="text-slate-500">Factura de origen:</span>{' '}
                  <button type="button" onClick={() => setDetalleId(detalle.facturaOrigen!.id)} className="font-medium text-blue-600 hover:text-blue-800">
                    {detalle.facturaOrigen.numeroFactura || `#${detalle.facturaOrigen.id}`}
                  </button>
                </div>
              )}
              {esNotaCredito(detalle) ? (<>
                <div title="Parte de la nota que bajó lo que quedaba por cobrar de su factura de origen"><span className="text-slate-500">Aplicado a la factura:</span> <span className="font-medium text-emerald-700">{pesos(detalle.montoAplicadoOrigen ?? 0)}</span></div>
                <div title="Lo que no entró en lo pendiente de la factura: queda a favor del cliente"><span className="text-slate-500">Saldo a favor:</span> <span className="font-medium">{pesos(detalle.saldoCredito ?? 0)}</span></div>
              </>) : (<>
                <div><span className="text-slate-500">Cobrado:</span> <span className="font-medium">{pesos(detalle.montoCobrado)}</span></div>
                {Number(detalle.notasCreditoAplicadas || 0) > 0 && (
                  <div><span className="text-slate-500">Notas de crédito aplicadas:</span> <span className="font-medium text-emerald-700">{`−${pesos(detalle.notasCreditoAplicadas!)}`}</span></div>
                )}
                <div><span className="text-slate-500">Saldo a cobrar:</span> <span className="font-medium">{pesos(saldoACobrar(detalle))}</span></div>
                <div className="col-span-2"><span className="text-slate-500">Condición de pago:</span> <span className="font-medium">{detalle.condicionPago?.nombre ?? 'Sin condición (contado o vencimiento manual)'}</span></div>
              </>)}
              <div><span className="text-slate-500">Comprobante:</span> <span className="font-medium">{detalle.numeroFactura || '—'}{detalle.tipoComprobante ? ` (cód. ${detalle.tipoComprobante})` : ''}</span></div>
              <div><span className="text-slate-500">CAE:</span> <span className="font-medium">{detalle.cae ? `${detalle.cae} · vence ${fechaCorta(detalle.caeVencimiento ?? undefined)}` : '—'}</span></div>
            </div>
            {esNotaCredito(detalle) && detalle.facturaOrigen?.ordenVenta?.canal === 'mercadolibre' && (
              <p className="text-slate-500">Factura de origen de MercadoLibre: la cobró MercadoPago, que hace los reintegros. Esta nota no cambia lo que se cobra ni genera saldo a favor.</p>
            )}
            {(detalle.notasAsociadas?.length ?? 0) > 0 && (
              <div>
                <p className="font-medium mb-1">Notas de crédito y débito</p>
                <ul className="space-y-1">
                  {detalle.notasAsociadas!.map(n => (
                    <li key={n.id} className="flex flex-wrap items-center gap-2">
                      <BadgeTipo tipo={n.tipo} />
                      <button type="button" onClick={() => setDetalleId(n.id)} className="font-medium text-blue-600 hover:text-blue-800">{n.numeroFactura || `#${n.id}`}</button>
                      <span className="text-slate-500">{fechaCorta(n.fechaEmision)}</span>
                      <span className={n.tipo === 'nota_credito' ? 'text-emerald-700' : ''}>{n.tipo === 'nota_credito' ? `−${pesos(n.montoTotal)}` : pesos(n.montoTotal)}</span>
                      <span className="text-xs text-slate-500">{estadoVisible(n)}</span>
                      {n.tipo === 'nota_credito' && n.estado !== 'cancelada' && Number(n.saldoCredito || 0) > 0 && (
                        <span className="text-xs text-slate-500">{`saldo a favor ${pesos(n.saldoCredito!)}`}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {[...(detalle.aplicacionesCreditoRecibidas ?? []), ...(detalle.aplicacionesCreditoConsumidas ?? [])].length > 0 && (
              <div>
                <p className="font-medium mb-1">{esNotaCredito(detalle) ? 'Saldo a favor aplicado a otras facturas' : 'Saldo a favor aplicado'}</p>
                <ul className="space-y-1">
                  {[
                    ...(detalle.aplicacionesCreditoRecibidas ?? []).map(a => ({ a, facturaId: detalle.id, otro: a.notaCredito })),
                    ...(detalle.aplicacionesCreditoConsumidas ?? []).map(a => ({ a, facturaId: a.facturaVenta?.id ?? 0, otro: a.facturaVenta })),
                  ].map(({ a, facturaId, otro }) => (
                    <li key={a.id} className="flex flex-wrap items-center gap-2">
                      {otro ? (
                        <button type="button" onClick={() => setDetalleId(otro.id)} className="font-medium text-blue-600 hover:text-blue-800">{otro.numeroFactura || `#${otro.id}`}</button>
                      ) : <span className="font-medium">—</span>}
                      <span className="text-slate-500">{fechaCorta(a.createdAt)}{a.usuario ? ` · ${a.usuario}` : ''}</span>
                      <span className="text-emerald-700">{pesos(a.monto)}</span>
                      <button
                        type="button"
                        onClick={() => revertirAplicacion(facturaId, a.id)}
                        disabled={!can('cobros', 'eliminar') || !facturaId || revirtiendoAplicacion === a.id}
                        title={can('cobros', 'eliminar') ? 'La nota de crédito recupera el saldo y la factura vuelve a deber ese monto' : 'No tenés permiso para realizar esta acción'}
                        className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >{revirtiendoAplicacion === a.id ? 'Revirtiendo...' : 'Revertir'}</button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {esNotaCredito(detalle) && (detalle.devolucionesSaldo?.length ?? 0) > 0 && (
              <div>
                <p className="font-medium mb-1">Saldo a favor devuelto al cliente</p>
                <ul className="space-y-1">
                  {detalle.devolucionesSaldo!.map(d => (
                    <li key={d.id} className="flex flex-wrap items-center gap-2">
                      <span className="text-slate-500">
                        {[fechaCorta(d.devolucion?.fecha), d.devolucion?.medio, d.devolucion?.cuenta?.nombre, d.devolucion?.usuario].filter(Boolean).join(' · ')}
                      </span>
                      <span className="text-emerald-700">{pesos(d.monto)}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500 mt-1">Las devoluciones se revierten desde Cobros → Devolver saldo a favor.</p>
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              {!esNota(detalle) && detalle.estado !== 'cancelada' && (<>
                <Button
                  variant="secondary"
                  onClick={() => setNotaCreditoDe({ id: detalle.id, numeroFactura: detalle.numeroFactura, letra: detalle.letra })}
                  disabled={!can('facturas-venta', 'crear')}
                  title={!can('facturas-venta', 'crear') ? 'No tenés permiso para realizar esta acción' : 'Acreditar renglones de esta factura (devolución o bonificación)'}
                >Nota de crédito</Button>
                <Button
                  variant="secondary"
                  onClick={() => setNotaDebitoDe({ id: detalle.id, numeroFactura: detalle.numeroFactura, letra: detalle.letra })}
                  disabled={!can('facturas-venta', 'crear') || detalle.ordenVenta?.canal === 'mercadolibre'}
                  title={!can('facturas-venta', 'crear') ? 'No tenés permiso para realizar esta acción'
                    : detalle.ordenVenta?.canal === 'mercadolibre' ? 'Las ventas de MercadoLibre las cobra MercadoPago: no admiten nota de débito'
                      : 'Cargar un importe adicional sobre esta factura'}
                >Nota de débito</Button>
              </>)}
              {esNota(detalle) && detalle.estado !== 'cancelada' && (
                <Button
                  variant="danger"
                  onClick={anularNota}
                  loading={anulando}
                  disabled={!can('facturas-venta', 'editar')}
                  title={!can('facturas-venta', 'editar') ? 'No tenés permiso para realizar esta acción' : undefined}
                >{esNotaCredito(detalle) ? 'Anular nota de crédito' : 'Anular nota de débito'}</Button>
              )}
            </div>
            <div>
              <p className="font-medium mb-1">IVA por alícuota</p>
              {detalle.alicuotasIva && detalle.alicuotasIva.length > 0 ? (
                <TablaIvaPorAlicuota filas={detalle.alicuotasIva.map(a => ({ alicuota: Number(a.alicuota), netoGravado: Number(a.netoGravado), iva: Number(a.iva) }))} />
              ) : (
                <p className="text-slate-500">Factura anterior al desglose de IVA (sin totales por alícuota).</p>
              )}
            </div>
            {!esNotaCredito(detalle) && <div>
              <p className="font-medium mb-1">Cuotas</p>
              {detalle.cuotas && detalle.cuotas.length > 0 ? (
                <table className="w-full">
                  <thead><tr className="text-left text-slate-500"><th className="py-1">Cuota</th><th>Vencimiento</th><th className="text-right">Monto</th><th className="text-right">Cobrado</th><th className="text-right">Estado</th></tr></thead>
                  <tbody>
                    {detalle.cuotas.map(c => (
                      <tr key={c.id} className="border-t">
                        <td className="py-1">{`${c.numero}/${detalle.cuotas!.length}`}</td>
                        <td>{fechaCorta(c.fechaVencimiento)}</td>
                        <td className="text-right">{pesos(c.monto)}</td>
                        <td className="text-right">{pesos(c.montoCobrado)}</td>
                        <td className="text-right">{estadoCuota(c)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-slate-500">Factura sin cronograma de cuotas: vence entera el {fechaCorta(detalle.fechaVencimiento)}.</p>
              )}
            </div>}
          </div>
        )}
      </Modal>
      <NotaCreditoVentaModal factura={notaCreditoDe} onClose={() => setNotaCreditoDe(null)} onCreada={notaCreada} />
      <NotaDebitoVentaModal factura={notaDebitoDe} onClose={() => setNotaDebitoDe(null)} onCreada={notaCreada} />
    </div>
  );
}
